import { useMemo, useState } from 'react';
import { Bug, Monitor } from 'lucide-react';
import {
  bugSeverityLabels,
  bugStatusLabels,
  bugStatuses,
  type BugReportRecord,
  type BugStatus,
} from '@forq/platform-data';

interface BugsFeatureProps {
  readonly reports: readonly BugReportRecord[];
  readonly canManage: boolean;
  readonly busyId: string | null;
  readonly onSetStatus: (reportId: string, status: BugStatus, note: string) => void;
}

const zeitpunkt = (iso: string) =>
  iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–';

/**
 * Fehlermeldungen aus der Alphaphase bearbeiten.
 *
 * Standardmäßig stehen die offenen oben und die erledigten sind ausgeblendet:
 * eine Liste, in der Behobenes mitscrollt, wird nach zwei Wochen nicht mehr
 * geöffnet.
 */
export function BugsFeature({ reports, canManage, busyId, onSetStatus }: BugsFeatureProps) {
  const [zeigeErledigte, setZeigeErledigte] = useState(false);
  const [notizen, setNotizen] = useState<Record<string, string>>({});

  const offen = useMemo(
    () => reports.filter((eintrag) => eintrag.status === 'neu' || eintrag.status === 'in_arbeit'),
    [reports],
  );
  const sichtbar = zeigeErledigte ? reports : offen;

  return (
    <section className="admin-feature" aria-labelledby="admin-bugs-title">
      <div className="admin-feature__heading">
        <div>
          <span className="admin-eyebrow">Rückmeldungen</span>
          <h2 id="admin-bugs-title">Was ist den Kollegen aufgefallen?</h2>
        </div>
        <label className="admin-bugs__toggle">
          <input
            type="checkbox"
            checked={zeigeErledigte}
            onChange={(event) => setZeigeErledigte(event.target.checked)}
          />
          Erledigte einblenden
        </label>
      </div>

      <p className="admin-bugs__summary">
        {offen.length === 0
          ? 'Nichts Offenes.'
          : `${offen.length} offen${offen.length === 1 ? 'e Meldung' : 'e Meldungen'} von insgesamt ${reports.length}.`}
      </p>

      {sichtbar.length === 0 ? (
        <p className="admin-bugs__empty">
          <Bug size={15} /> Noch keine Meldungen. Der Knopf sitzt oben rechts in der Kopfzeile.
        </p>
      ) : (
        <div className="admin-bugs">
          {sichtbar.map((eintrag) => (
            <article key={eintrag.id} className={`admin-bug is-${eintrag.status} severity-${eintrag.severity}`}>
              <header>
                <div>
                  <strong>{eintrag.title}</strong>
                  <small>
                    {eintrag.reportedByName} · {zeitpunkt(eintrag.createdAt)} · {bugSeverityLabels[eintrag.severity]}
                  </small>
                </div>
                <span className={`admin-bug__status is-${eintrag.status}`}>{bugStatusLabels[eintrag.status]}</span>
              </header>

              <p className="admin-bug__text">{eintrag.description}</p>

              {/* Der Kontext ist der Grund, warum der Knopf existiert -- ohne ihn
                  kostet jede Meldung eine Rückfrage. Also steht er hier. */}
              <dl className="admin-bug__context">
                <div><dt>Seite</dt><dd>{eintrag.route || '–'}</dd></div>
                <div><dt>Fassung</dt><dd>{eintrag.appVersion || '–'}</dd></div>
                <div><dt><Monitor size={11} /> Fenster</dt><dd>{eintrag.viewport || '–'}</dd></div>
                <div><dt>Browser</dt><dd>{eintrag.userAgent || '–'}</dd></div>
              </dl>

              {eintrag.resolutionNote && (
                <p className="admin-bug__note">
                  <strong>{eintrag.handledByName ?? 'Bearbeitet'}:</strong> {eintrag.resolutionNote}
                </p>
              )}

              {canManage && (
                <div className="admin-bug__actions">
                  <input
                    type="text"
                    value={notizen[eintrag.id] ?? ''}
                    placeholder={'Anmerkung für den Melder (Pflicht bei „Kein Fehler")'}
                    onChange={(event) => setNotizen((alt) => ({ ...alt, [eintrag.id]: event.target.value }))}
                  />
                  {bugStatuses
                    .filter((stand) => stand !== eintrag.status)
                    .map((stand) => (
                      <button
                        key={stand}
                        type="button"
                        disabled={busyId === eintrag.id}
                        onClick={() => onSetStatus(eintrag.id, stand, notizen[eintrag.id] ?? '')}
                      >
                        {bugStatusLabels[stand]}
                      </button>
                    ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
